import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAudioItem, getListenedTime, logListenedTime } from '@/APIs/audioApi';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { produce } from 'immer';

function useAudio({ id = null, fetchOnPageLoad = false, willUseLog = false } = {}) {
    const location = usePathname();

    const queryClient = useQueryClient();
    const shouldRefetchResumeList = useRef(false);
    /* queries */
    const {
        isLoading: listenedTimeLoading,
        data: listenedTimeData,
        error: listenedTimeError
    } = useQuery({
        queryKey: ['getListenedTime', id],
        queryFn: () => getListenedTime({ object_id: id }),
        staleTime: 30 * 60 * 1000,
        enabled: fetchOnPageLoad
    });

    const {
        isLoading: audioDataLoading,
        data: audioData,
        error: audioDataError
    } = useQuery({
        queryKey: ['getAudioItem', id],
        queryFn: () => getAudioItem({ id }),
        staleTime: 30 * 60 * 1000,
        enabled: fetchOnPageLoad
    });

    /* mutations */
    const { mutate: logListenedTimeMutate } = useMutation({
        mutationFn: ({ time, duration, type, tags, sourcePageData, userAgentData }) => {
            return logListenedTime({
                object_id: id,
                time,
                percentage: Math.floor((time / duration) * 100),
                type,
                tags,
                sourcePageData,
                userAgentData
            });
        },
        onSuccess: (result) => {
            queryClient.setQueryData(['getListenedTime', id], () => result);
            shouldRefetchResumeList.current = true;
        }
    });

    useEffect(() => {
        return () => {
            if (
                audioData &&
                willUseLog &&
                shouldRefetchResumeList.current &&
                location.pathname !== '/'
            ) {
                const currentContent = queryClient.getQueryData(['getContent', null]);
                if (currentContent) {
                    queryClient.setQueryData(['getContent', null], (oldValue) => {
                        return produce(oldValue, (draft) => {
                            draft?.pages?.forEach((page) => {
                                page?.results?.sections?.forEach((section) => {
                                    if (
                                        section.content_generator === 'Stream Log' &&
                                        section.content_type === 'Audio'
                                    ) {
                                        if (
                                            !section.cards.find((card) => card.id === audioData.id)
                                        ) {
                                            const newCard = {
                                                id: audioData.id,
                                                title: audioData.title,
                                                media_object: {
                                                    posters: {
                                                        horizontal_poster:
                                                            audioData.horizontal_poster
                                                    }
                                                }
                                            };
                                            section.cards.push(newCard);
                                            /* if the watched media is new, the related card must be added to the cards list and
                                                                 these two queries must be reset in order to be sent again with new cards data as the param */
                                            queryClient.invalidateQueries({
                                                queryKey: ['getAudioResumeList']
                                            });
                                        }
                                    }
                                });
                            });
                        });
                    });
                }

                /* if the watched media is not new, meaning it is watched before,
                                         their related cards are in the cards cache, so only invalidating them is enough */
                queryClient.invalidateQueries({
                    queryKey: ['getAudioResumeList']
                });
            }
        };
    }, [audioData, location.pathname]);

    return {
        audioDataLoading,
        audioData,
        audioDataError,
        listenedTimeLoading,
        listenedTimeData,
        listenedTimeError,
        logListenedTimeMutate
    };
}

export default useAudio;
